import { z } from 'zod';

/**
 * The field types a template can declare. Two families:
 *
 *  - VALUE types (Text, MultiLine, Number, Date) are placed by substituting a
 *    `{{var_x}}` merge field with a formatted string. They differ only in how the
 *    value is formatted, never in how it is placed.
 *  - LAYOUT types (Boxed, Checkbox, Image) need their own placement pass on the
 *    server because the value does not land as a single run of text. Each is
 *    anchored by a Word bookmark.
 *
 * The strings are the wire format stored in SignCare's `Template.document_fields`
 * — do not rename them.
 */
export const TEMPLATE_FIELD_TYPES = [
  'Text',
  'MultiLine',
  'Number',
  'Date',
  'Boxed',
  'Checkbox',
  'Image',
] as const;

export type TemplateFieldType = (typeof TEMPLATE_FIELD_TYPES)[number];

/**
 * Where a field's value comes from at run time.
 *
 * `manual` — the person generating the document types it.
 * `step`   — it is auto-filled from a workflow step's verified result
 *            (`SessionStep.ResultJson[fieldKey]`), which is what dragging a field
 *            out of an API integration tile creates.
 *
 * SignCare stores the fields JSON verbatim, so this binding survives the
 * round-trip and a template stays self-describing.
 */
export const fieldBindingSchema = z.object({
  source: z.enum(['manual', 'step']),
  /** Workflow step that produces the value, e.g. `pan_verification`. */
  stepType: z.string().optional(),
  /** Key inside that step's result, e.g. `pan_number`. */
  fieldKey: z.string().optional(),
});

export type FieldBinding = z.infer<typeof fieldBindingSchema>;

/**
 * One field on a template. Mirrors `TemplateVaribleFields` in the SignCare portal
 * and `TemplateFieldOptions` on the server; every optional property is omitted when
 * unset so payloads stay byte-compatible with templates authored in the portal.
 */
export const templateFieldSchema = z.object({
  /** Label shown to whoever fills the form. */
  displayName: z.string(),
  /** Merge-field name — `{{var_X}}`. Derived from displayName, never typed. */
  fieldName: z.string(),
  required: z.boolean(),
  type: z.enum(TEMPLATE_FIELD_TYPES),

  // Image
  imageWidth: z.number().optional(),
  imageHeight: z.number().optional(),
  imageBorder: z.boolean().optional(),

  // Boxed
  /** Character boxes reserved. Omit to let the template's own table row decide. */
  boxCount: z.number().optional(),
  /** Forms that say "BLOCK LETTERS" want this. */
  uppercase: z.boolean().optional(),
  /** `blank` — a space fills a box; `skip` — spaces are dropped. */
  spacePolicy: z.enum(['blank', 'skip']).optional(),
  /** `reject` (default) or `truncate` when the value needs more boxes than exist. */
  overflowPolicy: z.enum(['reject', 'truncate']).optional(),

  // Date / Number
  /** .NET format string, e.g. `dd/MM/yyyy`. */
  dateFormat: z.string().optional(),
  decimalPlaces: z.number().optional(),
  thousandSeparator: z.boolean().optional(),

  // Checkbox
  options: z.array(z.string()).optional(),
  /** Glyph written into a ticked box. Keep to something the embedded font carries. */
  checkedMark: z.string().optional(),
  multiSelect: z.boolean().optional(),

  /** Layout types: the single character shown at the anchor. Never rendered. */
  displayTag: z.string().optional(),

  /** Studio-only: where the value comes from. Ignored by the SignCare renderer. */
  binding: fieldBindingSchema.optional(),
});

export type TemplateField = z.infer<typeof templateFieldSchema>;

/** The whole fields array as stored in `Template.document_fields`. */
export const templateFieldsSchema = z.array(templateFieldSchema);

/** Human-facing copy for the palette and the settings dialog. */
export const FIELD_TYPE_META: Record<
  TemplateFieldType,
  { label: string; hint: string; glyph: string }
> = {
  Text: { label: 'Text', hint: 'Single line of text', glyph: 'T' },
  MultiLine: { label: 'Paragraph', hint: 'Address or free text over several lines', glyph: '¶' },
  Number: { label: 'Number', hint: 'Amount or quantity', glyph: '#' },
  Date: { label: 'Date', hint: 'Formatted date', glyph: 'D' },
  Boxed: {
    label: 'Boxed',
    hint: 'One character per box — drop it in the first box of the row',
    glyph: '▢',
  },
  Checkbox: { label: 'Checkbox', hint: 'Tick one option out of a group', glyph: '☑' },
  Image: { label: 'Image', hint: 'Photograph or logo', glyph: '🖼' },
};

/**
 * Per-type defaults applied when a field is first dropped.
 *
 * Boxed defaults to uppercase + reject-on-overflow deliberately: a silently
 * truncated name on a KYC form is a compliance failure, not a cosmetic one.
 */
export function defaultsForType(type: TemplateFieldType): Partial<TemplateField> {
  switch (type) {
    case 'Image':
      return { imageWidth: 200, imageHeight: 150, imageBorder: false, displayTag: '*' };
    case 'Boxed':
      return {
        uppercase: true,
        spacePolicy: 'blank',
        overflowPolicy: 'reject',
        displayTag: '*',
      };
    case 'Checkbox':
      return { options: ['Yes'], checkedMark: '✓', multiSelect: false, displayTag: '*' };
    case 'Date':
      return { dateFormat: 'dd/MM/yyyy' };
    case 'Number':
      return { decimalPlaces: 2, thousandSeparator: false };
    default:
      return {};
  }
}

/**
 * Strips the properties that don't apply to a field's type before saving.
 *
 * Without this, changing a field from Image to Text in the settings dialog would
 * leave `imageWidth` behind — and the server's `ProcessFieldsJson` would have to
 * guess. Keeping the payload minimal keeps the round-trip lossless.
 */
export function pruneForType(field: TemplateField): TemplateField {
  const base = {
    displayName: field.displayName,
    fieldName: field.fieldName,
    required: field.required,
    type: field.type,
    ...(field.binding ? { binding: field.binding } : {}),
  } satisfies TemplateField;

  switch (field.type) {
    case 'Image':
      return {
        ...base,
        imageWidth: field.imageWidth,
        imageHeight: field.imageHeight,
        imageBorder: field.imageBorder,
        displayTag: field.displayTag,
      };
    case 'Boxed':
      return {
        ...base,
        boxCount: field.boxCount,
        uppercase: field.uppercase,
        spacePolicy: field.spacePolicy,
        overflowPolicy: field.overflowPolicy,
        displayTag: field.displayTag,
      };
    case 'Checkbox':
      return {
        ...base,
        options: field.options,
        checkedMark: field.checkedMark,
        multiSelect: field.multiSelect,
        displayTag: field.displayTag,
      };
    case 'Date':
      return { ...base, dateFormat: field.dateFormat };
    case 'Number':
      return {
        ...base,
        decimalPlaces: field.decimalPlaces,
        thousandSeparator: field.thousandSeparator,
      };
    default:
      return base;
  }
}
